import { Box, Center, Flex, Text, Tooltip, Wrap } from '@chakra-ui/react'
import type { BoxProps } from '@chakra-ui/react'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  DynamicIcon,
  DynamicIconName,
  EmptyStateBox,
  ItemWrapper,
} from '@wanda-space/noelle'
import hooray from '@wanda-space/noelle/illustrations/hooray.svg'
import { type ItemResponseDto, StorageItemState } from '@wanda-space/types'
import { ItemImage } from 'components/Item/Image/ItemImage'
import { Routes } from 'consts'
import { format } from 'date-fns'
import { useProductsAndCategories } from 'hooks/useProductsAndCategories'
import * as R from 'ramda'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { sanitizeAmount } from 'utils'

import { DeleteItem } from './DeleteItem'

export const getProductName = (item: ItemResponseDto) => {
  const { name, metadata } = item.storageProduct
  return name.length ? name : (metadata?.itemType?.toString().toLowerCase() ?? '')
}

export const itemMatchesSearchQuery = (item: ItemResponseDto, searchQuery: string) => {
  const normalizedSearchQuery = searchQuery.trim().toLowerCase()
  if (!normalizedSearchQuery.length) {
    return true
  }
  return (
    item.name.toLowerCase().includes(normalizedSearchQuery) ||
    (item.description ?? '').toLowerCase().includes(normalizedSearchQuery) ||
    getProductName(item).toLowerCase().includes(normalizedSearchQuery)
  )
}

interface ItemListProps {
  items: ItemResponseDto[]
  containerBoxProps?: BoxProps
  groupByProductType?: boolean
  hasM2Subscription?: boolean
  hidePrice?: boolean
  searchQuery?: string
  emptyStateBoxProps?: {
    titleLokalizeId?: string
    iconImgSrc?: string
    linkTo: string
    linkTextLokalizeId?: string
  }
  seeAllLink?: {
    pathToSeeAllPage: string
    showWithEmptyStateToo?: boolean
  }
}

export const StorageItemList = ({
  items,
  containerBoxProps = { mx: -3 },
  emptyStateBoxProps,
  groupByProductType,
  hasM2Subscription,
  hidePrice,
  searchQuery,
  seeAllLink,
}: ItemListProps) => {
  const { productCategoriesById } = useProductsAndCategories()
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<Set<string>>(new Set())

  const toggleGroupExpanded = (groupKey: string) => {
    setExpandedGroupKeys((prev) => {
      const next = new Set(prev)
      next.has(groupKey) ? next.delete(groupKey) : next.add(groupKey)
      return next
    })
  }

  const isSearching = (searchQuery ?? '').trim().length > 0

  const visibleItems = isSearching
    ? items.filter((item) => itemMatchesSearchQuery(item, searchQuery ?? ''))
    : items

  const sortedItems = R.sortBy(getProductName, visibleItems)

  const groupedByProduct = groupByProductType
    ? R.groupWith((a, b) => getProductName(a) === getProductName(b), sortedItems)
    : []

  const renderPaymentCommitmentBadge = (amountOfItemsWithPaymentCommitment: number) => (
    <Tooltip
      backgroundColor="purple.200"
      color="black.800"
      borderRadius="lg"
      p="4"
      hasArrow
      label={
        <Box>
          <Text fontWeight="medium">{formatMessage({ id: 'word.paymentcommitment' })}</Text>
          <Text fontWeight="light">{formatMessage({ id: 'paymentcommitment.description' })}</Text>
        </Box>
      }
    >
      <Flex
        px="2"
        borderRadius="2xl"
        bg="red.50"
        border="1px solid #fcdede"
        alignItems="center"
        gap="1"
        py="1"
      >
        <DynamicIcon h={4} w={4} iconName={DynamicIconName.lockIcon} color="red.500" />
        <Text fontSize="14px" color="red.500">
          {amountOfItemsWithPaymentCommitment}
        </Text>
      </Flex>
    </Tooltip>
  )

  const renderItemRow = (
    item: ItemResponseDto,
    options?: { price?: string; amountOfItemsWithPaymentCommitment?: number }
  ) => {
    const category = productCategoriesById?.[item.storageProduct?.categoryId ?? '']
    // @ts-ignore fix pendingOrderId
    const isBookedItem = !!item.pendingOrderId

    return (
      <ItemWrapper
        key={item.id}
        img={
          <ItemImage
            itemId={item.id}
            imageId={item.image ?? undefined}
            iconName={category?.iconName as DynamicIconName}
          />
        }
        contentProps={{
          name: item.name,
          type: category ? category.name : '',
          tag: isBookedItem ? formatMessage({ id: 'item.label.ordered' }) : '',
          createdAt: isBookedItem
            ? ''
            : formatMessage(
                { id: 'space.createdFrom' },
                { time: format(new Date(item.createdAt), 'MMM dd yyyy') }
              ),
        }}
        moreActionButton={
          <Flex alignItems="center" gap="4">
            {Boolean(options?.amountOfItemsWithPaymentCommitment) &&
              renderPaymentCommitmentBadge(options?.amountOfItemsWithPaymentCommitment ?? 0)}
            {options?.price && <Text fontWeight="bold">{options.price}</Text>}
            <span>
              {isBookedItem === false && item.state === StorageItemState.WITH_CUSTOMER && (
                <DeleteItem itemName={item.name} itemId={item.id} />
              )}
              <ChevronRightIcon w={6} h={6} />
            </span>
          </Flex>
        }
        onClick={() => navigate(Routes.ItemDetails(item.id))}
      />
    )
  }

  const showSeeAllLink =
    seeAllLink &&
    ((items.length === 0 && seeAllLink.showWithEmptyStateToo === true) || items.length > 0)

  const seeAllLinkMarginX =
    typeof containerBoxProps.mx === 'number' && Math.sign(containerBoxProps.mx) === -1
      ? Math.abs(containerBoxProps.mx)
      : 0

  return (
    <Box {...containerBoxProps}>
      {items.length === 0 && emptyStateBoxProps ? (
        <EmptyStateBox
          icon={<img src={emptyStateBoxProps.iconImgSrc || hooray} alt="" />}
          text={
            <>
              <Text mr="1">
                {formatMessage({
                  id: emptyStateBoxProps.titleLokalizeId || 'itemoverview.emptyState.title',
                })}
              </Text>
              <Link style={{ textDecoration: 'underline' }} to={emptyStateBoxProps.linkTo}>
                {formatMessage({
                  id: emptyStateBoxProps.linkTextLokalizeId || 'itemoverview.emptyState.link',
                })}
              </Link>
            </>
          }
        />
      ) : isSearching && visibleItems.length === 0 ? (
        <Text color="gray.500" py="4">
          {formatMessage({
            id: 'space.myItems.search.noResults',
            defaultMessage: 'Fant ingen ting som matcher søket ditt',
          })}
        </Text>
      ) : groupByProductType ? (
        <>
          {groupedByProduct.map((group) => {
            const amountOfProducts = group.length
            const firstItem = group[0]
            const rawName = getProductName(firstItem)
            const groupKey = rawName
            const formattedName = amountOfProducts > 1 ? `${amountOfProducts}x ${rawName}` : rawName
            const isGroupExpanded = expandedGroupKeys.has(groupKey) || isSearching

            const amountOfItemsWithPaymentCommitment = hasM2Subscription
              ? group.length
              : // @ts-ignore fix ItemResponseDto for paymentCommitment
                group.filter(({ paymentCommitment }) => paymentCommitment !== null).length

            const name = formattedName.charAt(0).toUpperCase() + formattedName.slice(1)
            const category = productCategoriesById?.[firstItem.storageProduct?.categoryId ?? '']
            const totalPrice =
              hidePrice || hasM2Subscription
                ? ''
                : (`${sanitizeAmount(
                    group.reduce((sum, { storageProduct }) => sum + storageProduct.price, 0)
                  )},-/${formatMessage({ id: 'word.monthShort' })}` ?? '')

            if (amountOfProducts === 1) {
              return renderItemRow(firstItem, {
                price: totalPrice,
                amountOfItemsWithPaymentCommitment,
              })
            }

            return (
              <Box key={groupKey}>
                <ItemWrapper
                  minWidth={20}
                  disableHover={true}
                  img={
                    <Center
                      bg="purple.200"
                      borderRadius="lg"
                      minHeight="65px"
                      minWidth="65px"
                      maxHeight="65px"
                      maxWidth="65px"
                      border={amountOfProducts > 1 ? '1px solid' : 'none'}
                      borderColor={amountOfProducts > 1 ? 'purple.600' : 'none'}
                      boxShadow={amountOfProducts > 1 ? '4px 4px 0 #9791E5' : 'auto'}
                    >
                      <ItemImage iconName={category?.iconName as DynamicIconName} />
                    </Center>
                  }
                  contentProps={{
                    name,
                  }}
                  onClick={
                    amountOfProducts > 1
                      ? () => toggleGroupExpanded(groupKey)
                      : () => navigate(Routes.ItemDetails(firstItem.id))
                  }
                  moreActionButton={
                    <Flex alignItems="center" gap="4">
                      {amountOfItemsWithPaymentCommitment > 0 &&
                        renderPaymentCommitmentBadge(amountOfItemsWithPaymentCommitment)}
                      <Text fontWeight="bold">{totalPrice}</Text>
                      {amountOfProducts > 1 &&
                        (isGroupExpanded ? (
                          <ChevronUpIcon w={6} h={6} />
                        ) : (
                          <ChevronDownIcon w={6} h={6} />
                        ))}
                    </Flex>
                  }
                />
                {amountOfProducts > 1 && isGroupExpanded && (
                  <Box pl="8">{group.map((item) => renderItemRow(item))}</Box>
                )}
              </Box>
            )
          })}
        </>
      ) : (
        visibleItems.map((item) => renderItemRow(item))
      )}
      {showSeeAllLink ? (
        <Wrap mx={seeAllLinkMarginX} mt="4">
          <Link style={{ textDecoration: 'underline' }} to={seeAllLink.pathToSeeAllPage}>
            {formatMessage({ id: 'word.seeAll' })}
          </Link>
        </Wrap>
      ) : null}
    </Box>
  )
}
